import { formatCurrency } from './utils.js';

/**
 * Quote Calculator - Real-time calculation engine for Tāmēt.lv
 * Handles client-side calculations with HTMX integration
 */
class QuoteCalculator {
    constructor() {
        this.debounceTimer = null;
        this.debounceDelay = 300; // milliseconds
        this.init();
    }
    
    init() {
        this.bindEvents();
        this.initializeHTMX();
    }
    
    bindEvents() {
        // Real-time calculation on input change
        document.addEventListener('input', (e) => {
            if (e.target.matches('.calc-input')) {
                this.handleInputChange(e.target);
            }
        });
        
        // Handle HTMX calculation responses
        document.addEventListener('htmx:afterSwap', (e) => {
            if (e.detail.target.id === 'quote-totals') {
                this.animateTotalChange();
            }
        });
        
        // Handle HTMX before request to show loading state
        document.addEventListener('htmx:beforeRequest', (e) => {
            if (e.detail.target.matches('.calc-input')) {
                this.showCalculatingState(e.detail.target);
            }
        });
        
        // Handle HTMX after settle to remove loading state
        document.addEventListener('htmx:afterSettle', (e) => {
            this.removeCalculatingState();
        });
    }
    
    initializeHTMX() {
        // Configure HTMX for calculation endpoints
        document.body.addEventListener('htmx:configRequest', (e) => {
            // Add CSRF token to all requests
            if (e.detail.verb !== 'GET') {
                const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value;
                if (csrfToken) {
                    e.detail.headers['X-CSRFToken'] = csrfToken;
                }
            }
        });
    }
    
    handleInputChange(input) {
        // Debounce rapid input changes
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.calculateLineItem(input);
        }, this.debounceDelay);
        
        // Immediate local calculation for instant feedback
        this.updateLocalCalculation(input);
    }
    
    calculateLineItem(input) {
        const lineItem = input.closest('.line-item-row');
        if (!lineItem) return;
        
        const quantityInput = lineItem.querySelector('.quantity-input');
        const unitCostInput = lineItem.querySelector('.unit-cost-input');
        
        if (!quantityInput || !unitCostInput) return;
        
        const quantity = parseFloat(quantityInput.value) || 0;
        const unitCost = parseFloat(unitCostInput.value) || 0;
        
        // Validate inputs
        if (this.validateInputs(quantity, unitCost)) {
            // The HTMX request is triggered directly from the input elements
            // No need to manually trigger here
        }
    }
    
    updateLocalCalculation(input) {
        // Immediate visual feedback before server response
        const lineItem = input.closest('.line-item-row');
        if (!lineItem) return;
        
        const quantityInput = lineItem.querySelector('.quantity-input');
        const unitCostInput = lineItem.querySelector('.unit-cost-input');
        
        if (!quantityInput || !unitCostInput) return;
        
        const quantity = parseFloat(quantityInput.value) || 0;
        const unitCost = parseFloat(unitCostInput.value) || 0;
        const subtotal = quantity * unitCost;
        
        const subtotalElement = lineItem.querySelector('.line-item-subtotal');
        if (subtotalElement) {
            subtotalElement.textContent = this.formatCurrency(subtotal);
            subtotalElement.classList.add('calculating');
        }
        
        // Update quote total estimate
        this.updateQuoteTotalEstimate();
    }
    
    updateQuoteTotalEstimate() {
        // Calculate estimated total from all line items
        let total = 0;
        document.querySelectorAll('.line-item-row').forEach(row => {
            const quantity = parseFloat(row.querySelector('.quantity-input')?.value) || 0;
            const unitCost = parseFloat(row.querySelector('.unit-cost-input')?.value) || 0;
            total += quantity * unitCost;
        });
        
        // Update estimated total display if exists
        const estimatedTotal = document.querySelector('.estimated-total');
        if (estimatedTotal) {
            estimatedTotal.textContent = this.formatCurrency(total);
            estimatedTotal.classList.add('calculating');
        }
    }
    
    validateInputs(quantity, unitCost) {
        return quantity >= 0 && unitCost >= 0 && 
               !isNaN(quantity) && !isNaN(unitCost);
    }
    
    formatCurrency(amount) {
        return formatCurrency(amount);
    }
    
    showCalculatingState(element) {
        // Add visual feedback during calculation
        const lineItem = element.closest('.line-item-row');
        if (lineItem) {
            lineItem.classList.add('calculating-row');
        }
        
        // Show calculating indicator on totals
        const totalsSection = document.getElementById('quote-totals');
        if (totalsSection) {
            totalsSection.classList.add('calculating');
        }
    }
    
    removeCalculatingState() {
        // Remove all calculating states
        document.querySelectorAll('.calculating-row').forEach(el => {
            el.classList.remove('calculating-row');
        });
        
        document.querySelectorAll('.calculating').forEach(el => {
            el.classList.remove('calculating');
        });
    }
    
    animateTotalChange() {
        const totalElement = document.querySelector('.quote-total');
        if (totalElement) {
            totalElement.classList.add('total-updated');
            setTimeout(() => {
                totalElement.classList.remove('total-updated');
            }, 1000);
        }
    }
    
    // Helper method to format number input
    formatNumberInput(input) {
        // Bound once per input (this runs again after every swap), and keeps
        // up to 4 decimals: rounding to 2 on blur changed quantities such as
        // 0.125 m³ that the model stores at 4 decimals.
        if (input.dataset.numberFormatBound) return;
        input.dataset.numberFormatBound = 'true';
        input.addEventListener('blur', function() {
            const value = parseFloat(this.value);
            if (!isNaN(value)) {
                this.value = String(Number(value.toFixed(4)));
            }
        });
    }
    
    // Initialize all number inputs
    initializeNumberInputs() {
        document.querySelectorAll('input[type="number"].calc-input').forEach(input => {
            this.formatNumberInput(input);
        });
    }
}

// Export init function for module system
export function init() {
    window.quoteCalculator = new QuoteCalculator();
    window.quoteCalculator.initializeNumberInputs();
}

// Re-initialize when HTMX swaps content
document.addEventListener('htmx:afterSwap', function(e) {
    if (window.quoteCalculator) {
        window.quoteCalculator.initializeNumberInputs();
    }
});

// Export the calculator class for advanced usage
export { QuoteCalculator };